import React, { Fragment, useMemo } from 'react'
import { ContentBlock } from 'components/ContentBlock'
import Divider from 'components/Divider'
import DataTable from 'components/DataTableTest'
import { DataTable as OldDataTable } from 'components/DataTable'
import {
	total_salesGroup,
	total_currency,
	total_credit,
	total_output,
	total_statistics,
	total_specialOps,
	total_rate
} from '../tableColumns'
import { Row, Col } from 'react-flexbox-grid'
import formatDate from 'helpers/dateTimeFormatter'
import { useTranslation } from 'react-i18next'
import useLocale from 'hooks/useLocale'
import get from 'lodash/get'
import { TextEditor } from 'components/forms'

import { useQuery, useMutation } from 'components/graphql'
import updateClosingMoney from '../gql/updateClosingMoney'
import getMainCurrency from '../gql/getMainCurrency'
import numberFormatter from 'helpers/numberFormatter'
import Button from 'components/Button'
import useClosingSum from '../hooks/useClosingSum'

const Total = ({ data, listQueryRef }) => {
	const result = useQuery(getMainCurrency, { fetchPolicy: 'cache-only' })
	const mainCurrency = get(result, 'data.getAppConfig.mainCurrency')
	const [t] = useTranslation()
	const locale = useLocale()
	const statistics = {
		[t('closing_section.strings.statistic_customer_count')]: numberFormatter(data.clientCount, { locale, toFixed: 0 }),
		[t('closing_section.strings.statistic_product_count')]: numberFormatter(data.productCount, { locale, toFixed: 0 }),
		[t('closing_section.strings.statistic_total_per_client')]: data.clientCount ? numberFormatter(data.total / data.clientCount, { locale }) : null,
		[t('closing_section.strings.statistic_products_per_client')]: data.clientCount ? numberFormatter(data.productCount / data.clientCount, { locale }) : null,
		[t('closing_section.strings.statistic_total_per_product')]: data.productCount ? numberFormatter(data.total / data.productCount, { locale }) : null,
		[t('closing_section.strings.statistic_cancelations')]: numberFormatter(data.cancellationsCount, { locale, toFixed: 0 }),
		[t('common.strings.from')]: formatDate(get(data, 'dateInterval.from'), 'DD.MM.YYYY HH:mm:ss'),
		[t('common.strings.to')]: formatDate(get(data, 'dateInterval.to'), 'DD.MM.YYYY HH:mm:ss'),
		[t('closing_section.strings.statistic_from_bill')]: numberFormatter(data.billNumStart, { locale, toFixed: 0 }),
		[t('closing_section.strings.statistic_to_bill')]: numberFormatter(data.billNumEnd, { locale, toFixed: 0 }),
		[t('closing_section.strings.statistic_count_from')]: formatDate(data.moneyCountDateTime, 'DD.MM.YYYY HH:mm:ss'),
		[t('closing_section.strings.statistic_door_counter')]: numberFormatter(data.doorCounter, { locale, toFixed: 0 }),
		...data.guestsCount && { [t('closing_section.strings.statistic_guests_count')]: numberFormatter(data.guestsCount, { locale, toFixed: 0 }) }
	}
	const [updatePresent] = useMutation(updateClosingMoney)
	const { toggleSum, isSum, loading, data: sumData } = useClosingSum({ variables: listQueryRef.current.variables })

	const updateData = (currency, newValue) => {
		let newValueEuro, newValueMainCurrency, listExtraCurrencies
		switch (currency) {
			case mainCurrency:
				newValueMainCurrency = newValue
				break
			case 'EUR':
				newValueEuro = newValue
				break
			default:
				const index = data.currency.findIndex(item => item.name === currency)
				listExtraCurrencies = []
				listExtraCurrencies[index] = newValue
		}
		updatePresent({
			variables: {
				id: data.id,
				fields: {
					euro_present: newValueEuro,
					chf_present: newValueMainCurrency,
					currencyPresent: listExtraCurrencies
				}
			},
			optimisticResponse: {
				__typename: 'Mutation',
				cashAssist: {
					__typename: 'CA_Mutation',
					updateClosingMoney: {
						...data
					}
				}
			}
		})
	}
	const prepareCashbookByDescription = (cashbooksData) => {
		const res = cashbooksData?.reduce((acc, curr) => {
			const currToSet = [{
				name: curr.description,
				amount: (curr.should || 0) - (curr.have || 0)
			}]
			return [...acc, ...currToSet]
		}, [])
		return res
	}
	const currencyData = useMemo(() => {
		const out = [...(data.currency || [])]
		if(!Array.isArray(data?.cashbook) || !data.cashbook.length || !data?.currency?.length) {
			return out
		}
		data.cashbook.forEach(cashbook => {
			const { should = 0, have = 0 } = cashbook || {}
			const mainCurrencyItem = { ...(out[0] || {}) }
			const balance = should - have
			out[0] = {
				...mainCurrencyItem,
				amount: mainCurrencyItem.amount + balance,
				amountConverted: mainCurrencyItem.amountConverted + balance,
				difference: mainCurrencyItem.difference - balance
			}
		})
		return out
	}, [data?.cashbook, data?.currency])

	return(
		<Fragment>
			<ContentBlock>
				<Button
					text={t('product_section.strings.sum')}
					status={isSum ? 'green' : 'primary'}
					onClick={toggleSum}
				/>
				<DataTable
					columns={total_salesGroup(t)}
					data={isSum ? sumData.salesGroups : data.salesGroups}
					loading={loading}
					// height={400}
					withSearch
					withFooter
					label={t('closing_section.sub_sections.closing')}
				/>
			</ContentBlock>
			<ContentBlock>
				<OldDataTable
					columns={total_currency(mainCurrency, updateData, locale)}
					data={currencyData}
					withSearch={false}
				/>
			</ContentBlock>
			<ContentBlock>
				<Row>
					<Col xs={6}>
						<DataTable
							columns={total_credit(t)}
							data={data.paymentMethods || []}
							withSearch={false}
						/>
					</Col>
					<Col xs={6}>
						<DataTable
							columns={total_output(t)}
							data={[...(data.coupons || []), ...(prepareCashbookByDescription(data.cashbook) || [])]}
							withSearch={false}
						/>
					</Col>
				</Row>
			</ContentBlock>
			<ContentBlock>
				<Row>
					<Col xs={6}>
						<DataTable
							columns={total_statistics(t)}
							data={Object.entries(statistics)}
							withSearch={false}
						/>
					</Col>
					<Col xs={6}>
						<DataTable
							columns={total_specialOps(t)}
							data={data.specialSales || []}
							withSearch={false}
						/>
						<Divider height={20}/>
						<DataTable
							columns={total_rate(t, locale)}
							data={data.taxes || []}
							withSearch={false}
						/>
					</Col>
				</Row>
			</ContentBlock>
			{data.inactive &&
				<ContentBlock>
					<p>{t('closing_section.strings.closing_is_inactive')}</p>
					<p>{t('closing_section.strings.closing_inactive_date')} {formatDate(data.inactiveDate, 'DD.MM.YYYY HH:mm:ss')}</p>
					<TextEditor
						label={t('closing_section.strings.closing_inactive_reason')}
						defaultText={data.inactiveReason}
						readOnly
						withToolbar={false}
						noForm
					/>
				</ContentBlock>
			}
		</Fragment>
	)
}

export default Total
